import {
  type Command,
  createGameKit,
  type DomainEventDefinition,
  type GameDefinition,
  type JsonValue,
  type RecordDefinition,
  type Snapshot,
  type Transaction,
} from "@e308/core";
import { initialAuthority, validateAuthority } from "./authority.js";
import { commandFor } from "./commands.js";
import { LIMITS } from "./content/setup.js";
import type {
  MatchEventPayload,
  NewBorderEventPayload,
  SeatEventPayload,
  SeatModel,
  ShieldEventPayload,
} from "./content-types.js";
import {
  emptyEventPayload,
  matchEventPayload,
  newBorderEventPayload,
  seatEventPayload,
  shieldEventPayload,
  validateConflictConfig,
} from "./definition-validation.js";
import { initialDiplomacy, validateDiplomacyForSetup } from "./diplomacy-state.js";
import type { DiplomacyState, MatchLogPayload } from "./diplomacy-types.js";
import { type Micro, microNumbers } from "./exact-numbers.js";
import { initialLifecycle, type LockedMatchSetup, lockMatchSetup } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { validateLifecycleForSetup } from "./lifecycle-validation.js";
import { validateMatchLog } from "./log-events.js";
import { createSeatModel } from "./seat-model.js";
import { observeSeat } from "./seat-observation.js";
import { runConflictStep } from "./simulation.js";
import type {
  AuthorityState,
  CanonicalPhase,
  ConflictConfig,
  ConflictIntent,
  SeatState,
} from "./types.js";

export interface ConflictContent {
  readonly matchId: string;
  readonly stepMs: number;
  readonly definition: GameDefinition<Micro>;
  readonly seats: readonly SeatModel[];
  readonly seatById: ReadonlyMap<string, SeatModel>;
  readonly authority: RecordDefinition<AuthorityState & JsonValue>;
  readonly lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>;
  readonly diplomacy: RecordDefinition<DiplomacyState & JsonValue>;
  readonly setup: LockedMatchSetup;
  readonly shieldBreached: DomainEventDefinition<ShieldEventPayload>;
  readonly seatEliminated: DomainEventDefinition<SeatEventPayload>;
  readonly newBorder: DomainEventDefinition<NewBorderEventPayload>;
  readonly overtimeStarted: DomainEventDefinition<JsonValue>;
  readonly matchEnded: DomainEventDefinition<MatchEventPayload>;
  readonly matchLog: DomainEventDefinition<MatchLogPayload & JsonValue>;
  commandFor(seatId: string, intent: ConflictIntent): Command<Micro>;
  observe(snapshot: Snapshot<Micro>, seatId: string): SeatState;
  runStep(
    transaction: Transaction<Micro>,
    elapsedMs: number,
    phase: (phase: CanonicalPhase) => void,
  ): void;
}

export function createConflictContent(config: ConflictConfig): ConflictContent {
  validateConflictConfig(config);
  const setup = lockMatchSetup(config);
  const stepMs = config.stepMs ?? 250;
  const kit = createGameKit({ numbers: microNumbers });
  const world = kit.scope("world");
  const seatIds = config.seats.map((seat) => seat.id);
  const seats = config.seats.map((seat) => createSeatModel(kit, seat, seatIds));
  const seatById = new Map(seats.map((seat) => [seat.seat.id, seat]));
  const authority = kit.record("authority", {
    scope: world,
    version: 1,
    initial: initialAuthority(config.seats),
    validate: validateAuthority,
  });
  const lifecycle = kit.record("match-lifecycle", {
    scope: world,
    version: 1,
    initial: initialLifecycle(setup),
    validate: (value) => validateLifecycleForSetup(value, setup, seatIds),
  });
  const eventSetting = config.eventSetting ?? "off";
  const diplomacy = kit.record("diplomacy", {
    scope: world,
    version: 1,
    initial: initialDiplomacy(eventSetting),
    validate: (value) => validateDiplomacyForSetup(value, eventSetting, seatIds),
  });
  const events = createConflictEvents(kit);
  const definition = defineConflictGame(kit, {
    config,
    seats,
    setup,
    stepMs,
    authority,
    lifecycle,
    diplomacy,
    domainEvents: Object.values(events),
  });
  const content: ConflictContent = {
    matchId: config.matchId ?? "local-match",
    stepMs,
    definition,
    seats: Object.freeze(seats),
    seatById,
    authority,
    lifecycle,
    diplomacy,
    setup,
    ...events,
    commandFor: (seatId, intent) =>
      commandFor(
        requiredSeat(seatById, seatId),
        intent,
        seatById,
        lifecycle,
        diplomacy,
        events.matchLog,
        setup,
        stepMs,
      ),
    observe: (snapshot, seatId) => observeSeat(snapshot, requiredSeat(seatById, seatId), lifecycle),
    runStep: (transaction, elapsedMs, phase) =>
      runConflictStep(transaction, seats, elapsedMs, phase, lifecycle, diplomacy, setup, events),
  };
  return Object.freeze(content);
}

function createConflictEvents(kit: ReturnType<typeof createGameKit<Micro>>) {
  return Object.freeze({
    shieldBreached: kit.eventType("shield-breached", {
      version: 1,
      validate: shieldEventPayload,
    }),
    seatEliminated: kit.eventType("seat-eliminated", {
      version: 1,
      validate: seatEventPayload,
    }),
    newBorder: kit.eventType("new-border", { version: 1, validate: newBorderEventPayload }),
    overtimeStarted: kit.eventType("overtime-started", {
      version: 1,
      validate: emptyEventPayload,
    }),
    matchEnded: kit.eventType("match-ended", { version: 1, validate: matchEventPayload }),
    matchLog: kit.eventType("match-log", { version: 1, validate: validateMatchLog }),
  });
}

interface DefinitionParts {
  readonly config: ConflictConfig;
  readonly seats: readonly SeatModel[];
  readonly setup: LockedMatchSetup;
  readonly stepMs: number;
  readonly authority: RecordDefinition<AuthorityState & JsonValue>;
  readonly lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>;
  readonly diplomacy: RecordDefinition<DiplomacyState & JsonValue>;
  readonly domainEvents: readonly DomainEventDefinition[];
}

function defineConflictGame(
  kit: ReturnType<typeof createGameKit<Micro>>,
  parts: DefinitionParts,
): GameDefinition<Micro> {
  const { config, seats, setup, stepMs, authority, lifecycle, diplomacy, domainEvents } = parts;
  return kit.defineGame({
    id: "kilnfront",
    simulationVersion: 5,
    stepMs,
    rootSeed: config.rootSeed ?? "00",
    eventRetention: LIMITS.retainedEvents,
    resources: seats.flatMap((seat) => {
      const { wreckage, ...resources } = seat.resources;
      return [...Object.values(resources), ...Object.values(wreckage)];
    }),
    allocations: seats.flatMap((seat) => [
      seat.powerOperation,
      seat.factoryWorksSplit,
      seat.constructionSplit,
      seat.targeting,
      seat.supplyAllocation,
      seat.shieldAllocation,
    ]),
    records: [
      authority,
      lifecycle,
      diplomacy,
      ...seats.flatMap((seat) => [
        seat.economySettings,
        seat.manufacturing,
        seat.research,
        seat.logistics,
        seat.combat,
      ]),
    ],
    domainEvents,
    win: (state) =>
      new Set(
        seats
          .filter((seat) => state.get(seat.resources.headquarters) > 0n)
          .map((seat) => setup.teamBySeat[seat.seat.id]),
      ).size <= 1,
  });
}

function requiredSeat(seats: ReadonlyMap<string, SeatModel>, seatId: string): SeatModel {
  const seat = seats.get(seatId);
  if (!seat) throw new TypeError(`Unknown seat: ${seatId}`);
  return seat;
}
